/**
 * Score Utilities
 *
 * Utility functions for OKR score color coding and labels
 */

import { CompletionStatusOption } from '../types';

// =============================================================================
// SCORE COLOR CODING
// =============================================================================

/**
 * Get color based on score percentage
 * 100% = Green (Complete)
 * 50-99% = Yellow (Good Progress)
 * 20-49% = Orange (Needs Attention)
 * 1-19% = Red (At Risk)
 * 0% = Gray (Not Started)
 */
export const getScoreColor = (score: number): string => {
    if (score >= 100) return '#22c55e'; // Green
    if (score >= 50) return '#eab308';  // Yellow
    if (score >= 20) return '#f97316';  // Orange
    if (score >= 1) return '#ef4444';   // Red
    return '#6b7280';                   // Gray
};

/**
 * Get label based on score percentage
 */
export const getScoreLabel = (score: number): string => {
    if (score >= 100) return 'Complete';
    if (score >= 50) return 'Good Progress';
    if (score >= 20) return 'Needs Attention';
    if (score >= 1) return 'At Risk';
    return 'Not Started';
};

// =============================================================================
// COMPLETION STATUS OPTIONS
// =============================================================================

export const COMPLETION_STATUS_OPTIONS: CompletionStatusOption[] = [
    { value: 'not_started', label: 'Not Started', color: '#94a3b8' },
    { value: 'less_than_25', label: 'Less than 25%', color: '#ef4444' },
    { value: 'less_than_50', label: 'Less than 50%', color: '#f97316' },
    { value: 'almost_75', label: 'Almost 75%', color: '#eab308' },
    { value: 'almost_100', label: 'Almost 100%', color: '#22c55e' },
    { value: 'completed', label: '100% Complete', color: '#10b981' }
];

/**
 * Get numeric score from status string
 */
export const getScoreFromStatus = (status: string): number => {
    const mapping: Record<string, number> = {
        'not_started': 0,
        'less_than_25': 12.5,
        'less_than_50': 37.5,
        'almost_75': 62.5,
        'almost_100': 87.5,
        'completed': 100
    };
    return mapping[status] ?? 0;
};

/**
 * Get status string from numeric score
 */
export const getStatusFromScore = (score: number): string => {
    if (score >= 100) return 'completed';
    if (score >= 75) return 'almost_100';
    if (score >= 50) return 'almost_75';
    if (score >= 25) return 'less_than_50';
    if (score > 0) return 'less_than_25';
    return 'not_started';
};

/**
 * Get color for a completion status value
 */
export const getStatusColor = (status: string): string => {
    const option = COMPLETION_STATUS_OPTIONS.find(opt => opt.value === status);
    return option?.color ?? '#94a3b8';
};
